import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, BellRing, TrendingUp, TrendingDown, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export function OddsAlerts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [alertType, setAlertType] = useState('odds_drop');
  const [threshold, setThreshold] = useState('');

  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['odds-alerts', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('odds_alerts')
        .select('*, live_matches(home_team, away_team, sport, league)')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: liveMatches } = useQuery({
    queryKey: ['live-matches-for-alerts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('live_matches')
        .select('*')
        .in('match_status', ['upcoming', 'live'])
        .order('start_time');
      if (error) throw error;
      return data;
    },
  });

  const createAlert = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('odds_alerts').insert({
        user_id: user?.id,
        match_id: selectedMatch,
        alert_type: alertType,
        threshold_value: parseFloat(threshold),
        is_active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['odds-alerts'] });
      toast.success('Alert created!');
      setShowCreate(false);
      setSelectedMatch('');
      setThreshold('');
    },
    onError: () => toast.error('Failed to create alert'),
  });

  const deleteAlert = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase.from('odds_alerts').delete().eq('id', alertId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['odds-alerts'] });
      toast.success('Alert deleted');
    },
  });

  const toggleAlert = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('odds_alerts')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['odds-alerts'] }),
  });

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'odds_drop':
        return <TrendingDown className="h-4 w-4 text-green-500" />;
      case 'odds_rise':
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BellRing className="h-5 w-5 text-primary" />
                Odds Alerts
              </CardTitle>
              <CardDescription>Get notified when odds move in your favor</CardDescription>
            </div>
            <Button onClick={() => setShowCreate(!showCreate)} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Alert
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showCreate && (
            <div className="p-4 bg-muted/50 rounded-lg mb-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Match</Label>
                  <Select value={selectedMatch} onValueChange={setSelectedMatch}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select match" />
                    </SelectTrigger>
                    <SelectContent>
                      {liveMatches?.map((match) => (
                        <SelectItem key={match.id} value={match.id}>
                          {match.home_team} vs {match.away_team}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Alert Type</Label>
                  <Select value={alertType} onValueChange={setAlertType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="odds_drop">Odds Drop</SelectItem>
                      <SelectItem value="odds_rise">Odds Rise</SelectItem>
                      <SelectItem value="new_tip">New Tip Posted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Threshold (%)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 5"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                  />
                </div>
              </div>
              <Button
                onClick={() => createAlert.mutate()}
                disabled={!selectedMatch || !threshold}
                className="w-full"
              >
                Create Alert
              </Button>
            </div>
          )}

          {alertsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
          ) : alerts?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No alerts set up yet</p>
              <p className="text-sm">Create your first alert to get notified of odds changes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts?.map((alert: any) => (
                <div
                  key={alert.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getAlertIcon(alert.alert_type)}
                    <div>
                      <p className="font-medium">
                        {alert.live_matches?.home_team} vs {alert.live_matches?.away_team}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {alert.alert_type.replace('_', ' ')} by {alert.threshold_value}%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {alert.is_triggered && (
                      <Badge variant="secondary" className="bg-green-500/20 text-green-400">
                        Triggered
                      </Badge>
                    )}
                    <Switch
                      checked={alert.is_active}
                      onCheckedChange={(checked) =>
                        toggleAlert.mutate({ id: alert.id, isActive: checked })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteAlert.mutate(alert.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
